// Owner Dashboard — request list with status badges and response counts
import { Link } from "react-router-dom";
import { PlusCircle, FileText, Eye, MapPin, Home, Calendar, DollarSign, MessageSquareText, MessageCircle, ClipboardCheck, CheckCircle } from "lucide-react";
import { useOwnerRequests } from "@/hooks/useOwnerRequests";
import { PageHeader, Card, StatusBadge, Button, EmptyState, LoadingSpinner } from "@/components/shared";
import {
  RequestStatus,
  RequestStatusLabels,
  ProvinceLabels,
  PropertyTypeLabels,
  TimelineLabels,
  type OwnerRequest,
} from "@/types";

// Rotating property placeholder images from Unsplash (small thumbs, free)
const PROPERTY_IMAGES = [
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=400&h=250&fit=crop",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=250&fit=crop",
];

function getStatusVariant(status: RequestStatus) {
  switch (status) {
    case RequestStatus.Draft: return "default" as const;
    case RequestStatus.Active: return "info" as const;
    case RequestStatus.ComparingAgents: return "warning" as const;
    case RequestStatus.ContactUnlocked: return "success" as const;
    case RequestStatus.Completed: return "success" as const;
    case RequestStatus.Deactivated: return "error" as const;
  }
}

export function OwnerDashboardPage() {
  const { requests, isLoading, error } = useOwnerRequests();

  if (isLoading) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  return (
    <div>
      <PageHeader
        title="My Requests"
        description="Manage your anonymous property requests and review agent responses."
        actions={
          <Link to="/owner/new-request">
            <Button>
              <PlusCircle className="h-4 w-4 mr-2" /> New Request
            </Button>
          </Link>
        }
      />

      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

      {requests.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No requests yet"
          description="Create your first anonymous owner request to start receiving agent proposals."
          action={
            <Link to="/owner/new-request">
              <Button>Create Request</Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-4">
          {requests.map((request, i) => (
            <RequestCard key={request.id} request={request} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function RequestCard({ request, index }: { request: OwnerRequest; index: number }) {
  const img = PROPERTY_IMAGES[index % PROPERTY_IMAGES.length];

  return (
    <Link to={`/owner/requests/${request.id}`} className="block group">
      <Card padding={false} className="overflow-hidden hover:border-gold/40 transition-all hover:shadow-lg hover:shadow-gold/5">
        <div className="flex">
          {/* Thumbnail */}
          <div className="w-40 h-32 flex-shrink-0 relative overflow-hidden">
            <img
              src={img}
              alt="Property"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-dark/80" />
          </div>

          {/* Content */}
          <div className="flex-1 p-4 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h3 className="text-base font-semibold text-white group-hover:text-gold transition-colors">
                  {request.suburb}, {ProvinceLabels[request.province]}
                </h3>
                <StatusBadge
                  status={RequestStatusLabels[request.status]}
                  variant={getStatusVariant(request.status)}
                />
              </div>
              <div className="flex items-center gap-3 text-xs text-white/50 mt-1">
                <span className="flex items-center gap-1">
                  <Home className="h-3 w-3" />
                  {PropertyTypeLabels[request.property_type]}
                </span>
                <span className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  R{(request.price_band_min / 1000000).toFixed(1)}M – R{(request.price_band_max / 1000000).toFixed(1)}M
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {TimelineLabels[request.timeline]}
                </span>
                <span className="capitalize">{request.request_type}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3">
                {request.response_count !== undefined && request.response_count > 0 && (
                  <span className="flex items-center gap-1 text-xs text-sun font-medium">
                    <MessageSquareText className="h-3 w-3" />
                    {request.response_count} response{request.response_count > 1 ? "s" : ""}
                  </span>
                )}
                {!!request.invited_count && (
                  <span className="text-xs text-mint font-medium">
                    ✓ {request.invited_count} invited
                  </span>
                )}
                {!!request.engaging_count && (
                  <span className="text-xs text-green-400 font-medium flex items-center gap-1">
                    <MessageCircle className="h-3 w-3" /> {request.engaging_count} engaging
                  </span>
                )}
                {!!request.contact_shared_count && (
                  <span className="text-xs text-blue-400 font-medium flex items-center gap-1">
                    <ClipboardCheck className="h-3 w-3" /> {request.contact_shared_count} contact shared
                  </span>
                )}
                {!!request.completed_count && (
                  <span className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> {request.completed_count} completed
                  </span>
                )}
                {!request.response_count && (
                  <span className="text-xs text-white/30">No responses yet</span>
                )}
              </div>
              <span className="text-xs text-white/30 group-hover:text-gold/60 transition-colors flex items-center gap-1">
                <Eye className="h-3 w-3" /> View
              </span>
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}
